/* eslint-disable react/no-unescaped-entities */
import { useEffect, useState } from 'react';
import axios from 'axios';
import { HOME } from '@/constants/testIds';

const API = process.env.REACT_APP_BACKEND_URL;

const Lightbox = ({ item, onClose }) => {
  useEffect(() => {
    if (!item) return undefined;
    const onKey = (e) => { if (e.key === 'Escape') onClose(); };
    document.addEventListener('keydown', onKey);
    const prevOverflow = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    return () => {
      document.removeEventListener('keydown', onKey);
      document.body.style.overflow = prevOverflow;
    };
  }, [item, onClose]);

  if (!item) return null;
  const isVideo = item.media_type === 'video';
  return (
    <div
      data-testid={HOME.galleryLightbox}
      onClick={onClose}
      className="fixed inset-0 z-[100] bg-black/85 backdrop-blur-md flex items-center justify-center p-6 sm:p-12"
    >
      <button
        type="button"
        onClick={onClose}
        aria-label="Close"
        data-testid={HOME.galleryClose}
        className="absolute top-6 right-6 w-12 h-12 rounded-full bg-white text-black font-display font-black text-xl flex items-center justify-center hover:bg-brand-orange transition-colors"
      >
        ✕
      </button>
      <div
        onClick={(e) => e.stopPropagation()}
        className="relative max-w-5xl w-full grid grid-cols-1 md:grid-cols-5 gap-6 items-center"
      >
        <div className="md:col-span-3 rounded-3xl overflow-hidden bg-black border-2 border-white/10 shadow-[10px_10px_0_#FF6600]">
          {isVideo ? (
            <video
              src={item.image}
              poster={item.poster || undefined}
              controls
              autoPlay
              playsInline
              className="w-full h-auto max-h-[75vh] bg-black"
            />
          ) : (
            <img
              src={item.image}
              alt={item.title}
              className="w-full h-auto max-h-[75vh] object-contain bg-black"
              draggable="false"
            />
          )}
        </div>
        <div className="md:col-span-2 text-white">
          <p className="font-body text-xs tracking-[0.4em] uppercase text-brand-orange mb-2">
            {item.category}
          </p>
          <h3 className="font-display font-black text-3xl sm:text-4xl leading-tight mb-4">
            {item.title}
          </h3>
          <p className="font-body text-base text-white/70 leading-relaxed">
            {item.description}
          </p>
        </div>
      </div>
    </div>
  );
};

const GalleryCard = ({ item, onOpen, index }) => {
  // Vary aspect ratio slightly to create a less-rigid grid
  const aspectClasses = ['aspect-[4/5]', 'aspect-square', 'aspect-[3/4]'];
  const aspect = aspectClasses[index % aspectClasses.length];
  const isVideo = item.media_type === 'video';

  return (
    <button
      type="button"
      data-testid={`${HOME.galleryCard}-${item.id}`}
      onClick={() => onOpen(item)}
      className="group relative block w-full text-left rounded-3xl overflow-hidden bg-black border-4 border-black shadow-[8px_8px_0_#FF6600] hover:shadow-[12px_12px_0_#FF6600] transition-shadow"
    >
      <div className={`relative ${aspect} overflow-hidden`}>
        {isVideo ? (
          <video
            src={item.image}
            poster={item.poster || undefined}
            muted
            loop
            playsInline
            preload="metadata"
            onMouseEnter={(e) => e.currentTarget.play().catch(() => {})}
            onMouseLeave={(e) => { e.currentTarget.pause(); e.currentTarget.currentTime = 0; }}
            className="absolute inset-0 w-full h-full object-cover transition-transform duration-500 group-hover:scale-105"
          />
        ) : (
          <img
            src={item.image}
            alt={item.title}
            className="absolute inset-0 w-full h-full object-cover transition-transform duration-500 group-hover:scale-105"
            draggable="false"
          />
        )}
        {/* gradient overlay */}
        <div className="absolute inset-0 bg-gradient-to-t from-black via-black/30 to-transparent" />
        {/* Category sticker + optional video badge */}
        <div className="absolute top-4 left-4 inline-flex items-center gap-2">
          <span className="bg-brand-orange text-black font-display font-black text-[10px] uppercase tracking-[0.2em] px-3 py-1.5 rounded-full">
            {item.category}
          </span>
          {isVideo && (
            <span className="bg-white text-black font-display font-black text-[10px] uppercase tracking-[0.2em] px-3 py-1.5 rounded-full inline-flex items-center gap-1">
              ▶ Video
            </span>
          )}
        </div>
        {/* Title + zoom prompt */}
        <div className="absolute bottom-0 left-0 right-0 p-5 flex items-end justify-between gap-3">
          <h3 className="font-display font-black text-white text-xl sm:text-2xl leading-tight">
            {item.title}
          </h3>
          <span className="shrink-0 w-10 h-10 rounded-full bg-white text-black font-display font-black flex items-center justify-center opacity-0 group-hover:opacity-100 translate-y-2 group-hover:translate-y-0 transition-all duration-300">
            {isVideo ? '▶' : '+'}
          </span>
        </div>
      </div>
    </button>
  );
};

const GallerySection = () => {
  const [items, setItems] = useState([]);
  const [error, setError] = useState(null);
  const [active, setActive] = useState(null);
  const [filter, setFilter] = useState('all');

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const { data } = await axios.get(`${API}/api/gallery`);
        if (!cancelled) setItems(data);
      } catch (e) {
        if (!cancelled) setError('Could not load the gallery right now.');
      }
    })();
    return () => { cancelled = true; };
  }, []);

  // Build the set of available categories from current items
  const categories = ['all', 'videos', ...Array.from(new Set(items.map((i) => i.category).filter(Boolean)))];
  const visible = items.filter((item) => {
    if (filter === 'all') return true;
    if (filter === 'videos') return item.media_type === 'video';
    return item.category === filter;
  });

  return (
    <section
      id="photo-dump"
      data-testid={HOME.gallerySection}
      className="relative bg-[#1a1a1a] text-white py-28 px-6 sm:px-12 overflow-hidden border-t-2 border-black"
    >
      {/* Background accents */}
      <div className="pointer-events-none absolute top-10 -left-32 w-[28rem] h-[28rem] rounded-full bg-brand-orange/10 blur-3xl" />
      <div className="pointer-events-none absolute bottom-0 right-0 w-[24rem] h-[24rem] rounded-full bg-white/5 blur-3xl" />

      <div className="max-w-6xl mx-auto relative">
        <div className="flex flex-col md:flex-row md:items-end md:justify-between gap-6 mb-14">
          <div>
            <p className="font-body text-xs sm:text-sm tracking-[0.4em] uppercase text-brand-orange mb-3">
              Caught on camera →
            </p>
            <h2 className="font-display font-black text-4xl sm:text-5xl md:text-6xl leading-[1.02] tracking-[-0.02em]">
              Photo<br />
              <span className="text-brand-orange">Dump.</span>
            </h2>
          </div>
          <p className="font-body text-sm text-white/60 max-w-sm">
            Real shirts, real people, real life. Straight from the studio's camera roll —
            tap any frame to see it full size.
          </p>
        </div>

        {error && (
          <div className="mb-6 px-5 py-4 rounded-2xl bg-red-500/10 border border-red-500/30 text-red-200 font-body text-sm">
            {error}
          </div>
        )}

        {/* Filter chips */}
        {items.length > 0 && (
          <div
            data-testid={HOME.galleryFilter}
            className="flex flex-wrap gap-2 mb-8"
          >
            {categories.map((cat) => {
              const active = filter === cat;
              const label = cat === 'all' ? 'All' : cat === 'videos' ? '▶ Videos' : cat;
              return (
                <button
                  key={cat}
                  type="button"
                  onClick={() => setFilter(cat)}
                  className={`px-4 py-2 rounded-full text-[11px] font-display font-black uppercase tracking-[0.2em] transition-colors border-2 ${
                    active
                      ? 'bg-brand-orange text-black border-brand-orange'
                      : 'bg-transparent text-white/70 border-white/15 hover:border-white/40 hover:text-white'
                  }`}
                >
                  {label}
                </button>
              );
            })}
          </div>
        )}

        {items.length === 0 && !error ? (
          <div
            data-testid={HOME.galleryEmpty}
            className="border-2 border-dashed border-white/20 rounded-3xl p-14 text-center text-white/60 font-body"
          >
            Camera roll is filling up — fresh frames landing soon.
          </div>
        ) : (
          <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-5 sm:gap-6">
            {visible.map((item, idx) => (
              <GalleryCard key={item.id} item={item} onOpen={setActive} index={idx} />
            ))}
          </div>
        )}

        <p className="font-body text-xs text-white/40 text-center mt-12 tracking-[0.25em] uppercase">
          Want a piece like one of these? <a href="#custom" className="text-brand-orange hover:underline">Submit your idea →</a>
        </p>
      </div>

      <Lightbox item={active} onClose={() => setActive(null)} />
    </section>
  );
};

export default GallerySection;
